/**
 * SINGLE SOURCE OF TRUTH for all Goguard packages.
 * All components (Pricing, LocationPage, Schemas, WhatsApp CTAs) MUST import from here.
 *
 * Package tiers (per owner directive Nov 2026 CRO overhaul):
 *   Guard Basic  — 4-camera IP system for terrace homes
 *   Guard Pro    — 8-camera IP system (most popular)
 *   Guard Elite  — 8-camera premium ColorVu with AcuSense AI + active deterrent
 *   Guard Ultra  — 16-camera AI system for banglo/commercial
 *
 * SCOPE: CCTV ONLY. No alarm, no smart lock, no smart home (owner directive Jun 2026).
 *
 * PRICING NOTE: Prices below are CONFIRMED FINAL by owner (Nov 2026).
 */

export const PACKAGES = [
  {
    id: 'guard-basic',
    name: 'Guard Basic',
    tagline: 'CCTV asas untuk rumah teres, siap dalam 1 hari.',
    bestFit: 'Rumah teres 1 tingkat / apartment kecil',
    price: 'RM1,800',
    priceNote: 'one-time, all-inclusive (Cat6 concealed)',
    priceStatus: 'confirmed_final',
    overview: {
      cameras: 4, resolution: '4MP', storage: '1TB',
      fit: 'Rumah teres / apartment',
      benefit: 'Live view dan playback dari telefon.',
      night: 'ColorVu tidak disenaraikan; model disahkan dalam quotation.',
      ai: 'Pengesanan AI tidak disenaraikan dalam spesifikasi pakej.',
    },
    features: [
      '4 kamera Hikvision IP 4MP outdoor IP66',
      'NVR 4-channel PoE + Surveillance HDD 1TB',
      'Kabel Cat6 full copper, concealed wiring',
      'Setup aplikasi telefon (live view + playback)',
      'Anggaran rakaman 15–20 hari (bergantung motion)',
      'Waranti workmanship 12 bulan bertulis',
    ],
    benefitLabels: [
      'Alert manusia',
      'Playback dari telefon',
      'Anggaran rakaman 15–20 hari',
    ],
    highlight: false,
  },
  {
    id: 'guard-pro',
    name: 'Guard Pro',
    tagline: 'Paling popular. 8 kamera IP untuk rumah teres 2 tingkat.',
    bestFit: 'Rumah teres 2 tingkat / rumah keluarga',
    price: 'RM4,500',
    priceNote: 'one-time, all-inclusive (Cat6 concealed)',
    priceStatus: 'confirmed_final',
    overview: {
      cameras: 8, resolution: '4MP', storage: '2TB',
      fit: 'Rumah keluarga / teres 2 tingkat',
      benefit: 'Rakaman malam berwarna dengan ColorVu.',
      night: 'ColorVu untuk rakaman malam berwarna.',
      ai: 'Notifikasi motion; AcuSense tidak disenaraikan dalam pakej ini.',
    },
    features: [
      '8 kamera Hikvision IP 4MP ColorVu IP66',
      'NVR 8-channel PoE + Surveillance HDD 2TB',
      'Kabel Cat6 full copper, concealed wiring',
      'Setup aplikasi telefon + notifikasi motion',
      'Anggaran rakaman 20–30 hari (bergantung motion)',
      'Waranti workmanship 12 bulan bertulis',
    ],
    benefitLabels: [
      'Malam berwarna (ColorVu)',
      'Alert manusia',
      'Anggaran rakaman sehingga 30 hari',
    ],
    highlight: true,
  },
  {
    id: 'guard-elite',
    name: 'Guard Elite',
    tagline: '8 kamera premium ColorVu dengan AcuSense AI dan deterrent aktif.',
    bestFit: 'Rumah teres premium / banglo kecil',
    price: 'RM6,500',
    priceNote: 'one-time, all-inclusive (Cat6 concealed)',
    priceStatus: 'confirmed_final',
    overview: {
      cameras: 8, resolution: '5MP', storage: '4TB',
      fit: 'Rumah premium / banglo kecil',
      benefit: 'ColorVu + AI tapis manusia dan kenderaan.',
      night: 'ColorVu untuk rakaman malam berwarna.',
      ai: 'AcuSense menapis manusia/kenderaan. Strobe dan two-way audio pada kamera terpilih.',
    },
    features: [
      '8 kamera Hikvision IP 5MP ColorVu + AcuSense AI human/vehicle',
      'NVR 8-channel PoE + Surveillance HDD 4TB',
      'Kabel Cat6 full copper, concealed wiring',
      'Strobe light + two-way audio pada kamera terpilih',
      'Notifikasi pintar (kurangkan false alert haiwan / pokok)',
      'Anggaran rakaman 30–45 hari (bergantung motion)',
      'Waranti workmanship 12 bulan bertulis',
    ],
    benefitLabels: [
      'Malam berwarna (ColorVu)',
      'AI kesan manusia / kenderaan',
      'Strobe + audio deterrent',
      'Rakaman sehingga 45 hari',
    ],
    highlight: false,
  },
  {
    id: 'guard-ultra',
    name: 'Guard Ultra',
    tagline: '16 kamera AI ColorVu untuk banglo dan kedai/pejabat.',
    bestFit: 'Banglo besar / kedai / pejabat SME',
    price: 'Dari RM12,000',
    priceNote: 'custom quotation, one-time (Cat6 concealed)',
    priceStatus: 'confirmed_final',
    overview: {
      cameras: 16, resolution: '5MP', storage: '8TB',
      fit: 'Banglo besar / kedai / pejabat',
      benefit: 'ColorVu + AI untuk lebih banyak sudut.',
      night: 'ColorVu untuk rakaman malam berwarna.',
      ai: 'AI mengikut model dalam quotation, akses multi-user dan pilihan kamera PTZ.',
    },
    features: [
      '16 kamera Hikvision IP 5MP ColorVu + AI face/vehicle',
      'NVR 16-channel PoE + Surveillance HDD 8TB',
      'Kabel Cat6 full copper, concealed wiring',
      'Pilihan kamera PTZ untuk perimeter besar',
      'Dashboard live view multi-user + akses berperingkat',
      'Anggaran rakaman 30–60 hari (bergantung motion)',
      'Waranti workmanship 12 bulan + priority service',
    ],
    benefitLabels: [
      'AI face recognition',
      'Pilihan kamera PTZ',
      'Dashboard multi-user',
      'Priority service',
    ],
    highlight: false,
  },
];

// Payment schedule: 50/50 (per owner directive Nov 2026)
export const PAYMENT_SCHEDULE = {
  deposit: {
    percent: 50,
    label: '50% deposit sebelum kerja bermula',
    detail: 'Untuk lock-in tarikh pemasangan dan order stok hardware.',
  },
  final: {
    percent: 50,
    label: '50% baki selepas handover siap',
    detail: 'Selepas kami test setiap kamera dan setup app di telefon anda.',
  },
  summary: '50/50: 50% deposit sebelum start, 50% baki selepas handover.',
};

export const getPackageById = (id) => PACKAGES.find((p) => p.id === id);
